/**
 * The canonical report tree (CORE-3). Rendered by the report route
 * (`/report/[sessionId]`) and by the onboarding sample modal, so a sample and
 * a real report are the same UI over the same persisted shape.
 *
 *   <ReportHeader/>      title, date, status, overall level badge, actions
 *   <ReportSummary/>     overall assessment + "what to work on next" (first on phones)
 *   <CompetencyList/>    C1..C8 cards; C1 "Observed", C2 exam-only note
 *   <DevelopmentPlan/>   practice plan + questions for the mentor session
 */
import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { InfoHint } from '@/components/tutorial/InfoHint';
import { LEVEL_META, type LevelKey } from '@/utils/icfLevels';
import type { PersistedAnalysisStructured, ReportEvidence } from '@/types/report';
import {
  competencyRows,
  developmentPlan,
  reportSummary,
  type CompetencyRow,
} from './reportModel';

// ---------------------------------------------------------------------------
// Level badge (one LEVEL_META table for every surface)
// ---------------------------------------------------------------------------

export function LevelBadge({
  levelKey,
  size = 'md',
  testID,
}: {
  levelKey: LevelKey | null;
  size?: 'md' | 'lg';
  testID?: string;
}) {
  const { t } = useTranslation();
  if (!levelKey) return null;
  const meta = LEVEL_META[levelKey];
  return (
    <View
      testID={testID}
      style={[
        styles.badge,
        size === 'lg' && styles.badgeLarge,
        { backgroundColor: meta.softColor, borderColor: meta.color },
      ]}
    >
      <Text style={[styles.badgeText, size === 'lg' && styles.badgeTextLarge, { color: meta.color }]}>
        {t(meta.labelKey)}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface ReportHeaderProps {
  title: string;
  meta?: string[];
  levelKey: LevelKey | null;
  /** Mentee target-framed headline; shown instead of the level badge. */
  levelText?: string | null;
  confidenceLabel?: string | null;
  statusLabel?: string | null;
  actions?: React.ReactNode;
}

export function ReportHeader({ title, meta = [], levelKey, levelText = null, confidenceLabel, statusLabel, actions }: ReportHeaderProps) {
  const { t } = useTranslation();
  const { isPhone } = useResponsive();
  return (
    <View testID="report_header" style={[styles.card, styles.header, isPhone && styles.cardPhone]}>
      <View style={[styles.headerTop, isPhone && styles.headerTopPhone]}>
        <View style={styles.flexOne}>
          <Text style={styles.eyebrow}>{t('report.eyebrow')}</Text>
          <Text selectable style={[styles.title, isPhone && styles.titlePhone]}>{title}</Text>
          {meta.filter(Boolean).map((line) => (
            <Text key={line} selectable style={styles.metaText}>{line}</Text>
          ))}
        </View>
        {levelKey || levelText ? (
          <View testID="report_overall_level" style={[styles.levelBlock, isPhone && styles.levelBlockPhone]}>
            <Text style={styles.levelLabel}>{t('report.overallLevel')}</Text>
            {levelText ? (
              <Text testID="report_mentee_headline" style={styles.menteeHeadline}>{levelText}</Text>
            ) : levelKey ? (
              <LevelBadge levelKey={levelKey} size="lg" />
            ) : null}
            <InfoHint id="report_level" />
            {!!confidenceLabel && (
              <Text style={styles.metaText}>{t('report.confidence', { label: confidenceLabel })}</Text>
            )}
          </View>
        ) : null}
      </View>
      {(!!statusLabel || !!actions) && (
        <View style={styles.headerActions}>
          {!!statusLabel && <Text style={styles.statusChip}>{statusLabel}</Text>}
          {actions}
        </View>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Summary + next steps
// ---------------------------------------------------------------------------

function ReportSummary({ structured }: { structured: PersistedAnalysisStructured }) {
  const { t } = useTranslation();
  const { isPhone } = useResponsive();
  const summary = useMemo(() => reportSummary(structured), [structured]);
  return (
    <View style={styles.stack}>
      <View testID="report_summary" style={[styles.card, isPhone && styles.cardPhone]}>
        <Text style={styles.sectionTitle}>{t('report.summaryTitle')}</Text>
        {!!summary.lead && <Text selectable style={styles.lead}>{summary.lead}</Text>}
        {!!summary.body && <Text selectable style={styles.body}>{summary.body}</Text>}
        {summary.reviewRecommended && (
          <View style={styles.notice}>
            <MaterialIcons name="flag" size={18} color={reviewDesk.amber} />
            <Text style={[styles.body, styles.flexOne]}>{t('report.reviewRecommended')}</Text>
          </View>
        )}
      </View>
      {(summary.nextSteps.length > 0 || !!summary.whyNotHigher) && (
        <View testID="report_next_steps" style={[styles.card, styles.accentCard, isPhone && styles.cardPhone]}>
          <Text style={styles.sectionTitle}>{t('report.nextStepsTitle')}</Text>
          {summary.nextSteps.map((step, index) => (
            <View key={step} style={styles.stepRow}>
              <Text style={styles.stepNumber}>{index + 1}</Text>
              <Text selectable style={[styles.body, styles.flexOne]}>{step}</Text>
            </View>
          ))}
          {!!summary.whyNotHigher && (
            <View style={styles.subBlock}>
              <Text style={styles.subTitle}>{t('report.whyNotHigher')}</Text>
              <Text selectable style={styles.body}>{summary.whyNotHigher}</Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Competencies
// ---------------------------------------------------------------------------

function Quote({ item, tone }: { item: ReportEvidence; tone: 'strength' | 'development' }) {
  return (
    <View style={[styles.quoteCard, tone === 'development' ? styles.quoteDevelopment : styles.quoteStrength]}>
      <View style={styles.quoteMeta}>
        {!!item.timestamp && <Text style={styles.timestamp}>{item.timestamp}</Text>}
      </View>
      <Text selectable style={styles.quote}>“{item.quote}”</Text>
      {!!item.rationale && <Text selectable style={styles.body}>{item.rationale}</Text>}
    </View>
  );
}

function PolicyChip({ policy }: { policy: CompetencyRow['policy'] }) {
  const { t } = useTranslation();
  if (!policy) return null;
  if (policy === 'ethics_concern') {
    return (
      <View testID="report_policy_chip_ethics_concern" style={[styles.badge, styles.concernChip]}>
        <MaterialIcons name="flag" size={14} color={reviewDesk.amber} />
        <Text style={[styles.badgeText, styles.concernText]}>{t('sampleReportPolicy.ethicsConcernChip')}</Text>
      </View>
    );
  }
  return (
    <View style={[styles.badge, styles.policyChip]}>
      <Text style={[styles.badgeText, styles.policyText]}>
        {policy === 'observed_only' ? t('sampleReportPolicy.observedChip') : '—'}
      </Text>
    </View>
  );
}

type CompetencyToggle = (code: CompetencyRow['code'], open: boolean) => void;

function CompetencyCard({
  row,
  initiallyOpen,
  onToggle,
}: {
  row: CompetencyRow;
  initiallyOpen: boolean;
  onToggle?: CompetencyToggle;
}) {
  const { t } = useTranslation();
  const { isPhone } = useResponsive();
  const [open, setOpen] = useState(initiallyOpen);
  const name = t(row.nameKey);
  const caption = row.policy === 'observed_only'
    ? t('sampleReportPolicy.c1Caption')
    : row.policy === 'ethics_concern'
      ? t('sampleReportPolicy.c1EthicsConcernCaption')
      : row.policy === 'exam_only'
        ? t('sampleReportPolicy.c2Caption')
        : null;
  return (
    <View testID={`report_competency_${row.code}`} style={styles.competency}>
      <Pressable
        testID={`report_competency_toggle_${row.code}`}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={t(open ? 'report.collapse' : 'report.expand', { competency: name })}
        onPress={() => {
          const next = !open;
          setOpen(next);
          onToggle?.(row.code, next);
        }}
        style={({ pressed }) => [styles.competencyHeader, pressed && styles.pressed]}
      >
        <Text style={styles.code}>{row.code}</Text>
        <View style={styles.flexOne}>
          <Text style={styles.competencyName}>{name}</Text>
          {!!caption && <Text style={styles.metaText}>{caption}</Text>}
        </View>
        {row.policy ? <PolicyChip policy={row.policy} /> : <LevelBadge levelKey={row.levelKey} />}
        <MaterialIcons name={open ? 'expand-less' : 'expand-more'} size={22} color={reviewDesk.muted} />
      </Pressable>
      {open && (
        <View style={[styles.competencyBody, isPhone && styles.competencyBodyPhone]}>
          {!!row.narrative && (
            <Text selectable testID={row.policy ? `report_policy_${row.code}` : undefined} style={styles.body}>
              {row.narrative}
            </Text>
          )}
          {row.strengths.length > 0 && (
            <View style={styles.subBlock}>
              <Text style={[styles.subTitle, styles.strengthTitle]}>{t('report.strengthsLabel')}</Text>
              {row.strengths.map((item, index) => (
                <Quote key={`${row.code}-s-${index}`} item={item} tone="strength" />
              ))}
            </View>
          )}
          {row.development.length > 0 && (
            <View style={styles.subBlock}>
              <Text style={[styles.subTitle, styles.developmentTitle]}>{t('report.developmentLabel')}</Text>
              {row.development.map((item, index) => (
                <Quote key={`${row.code}-d-${index}`} item={item} tone="development" />
              ))}
            </View>
          )}
          {row.inconsistentPatterns.map((pattern) => (
            <View key={pattern} style={styles.notice}>
              <MaterialIcons name="visibility" size={18} color={reviewDesk.amber} />
              <Text selectable style={[styles.body, styles.flexOne]}>
                {t('report.patternLabel')}: {pattern}
              </Text>
            </View>
          ))}
          {!row.policy && row.strengths.length === 0 && row.development.length === 0 && (
            <Text style={styles.metaText}>{t('report.noEvidence')}</Text>
          )}
        </View>
      )}
    </View>
  );
}

function CompetencyList({
  structured,
  onToggle,
}: {
  structured: PersistedAnalysisStructured;
  onToggle?: CompetencyToggle;
}) {
  const { t } = useTranslation();
  const { isPhone } = useResponsive();
  const rows = useMemo(() => competencyRows(structured), [structured]);
  return (
    <View testID="report_competencies" style={[styles.card, isPhone && styles.cardPhone]}>
      <Text style={styles.sectionTitle}>{t('report.competenciesTitle')}</Text>
      <Text style={styles.metaText}>{t('report.competenciesHint')}</Text>
      {rows.map((row) => (
        <CompetencyCard key={row.code} row={row} initiallyOpen={!isPhone && !row.policy} onToggle={onToggle} />
      ))}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Development plan
// ---------------------------------------------------------------------------

function DevelopmentPlan({ structured }: { structured: PersistedAnalysisStructured }) {
  const { t } = useTranslation();
  const { isPhone } = useResponsive();
  const plan = useMemo(() => developmentPlan(structured), [structured]);
  if (plan.practice.length === 0 && plan.mentorQuestions.length === 0) return null;
  return (
    <View testID="report_development_plan" style={[styles.card, isPhone && styles.cardPhone]}>
      <Text style={styles.sectionTitle}>{t('report.planTitle')}</Text>
      {plan.practice.length > 0 && (
        <View style={styles.subBlock}>
          <Text style={styles.subTitle}>{t('report.practiceTitle')}</Text>
          {plan.practice.map((item, index) => (
            <View key={`practice-${index}`} style={styles.planItem}>
              <Text selectable style={styles.bodyStrong}>{item.practice_focus}</Text>
              {!!item.evidence_anchor?.quote && (
                <Text selectable style={styles.metaText}>
                  {item.evidence_anchor.timestamp ? `${item.evidence_anchor.timestamp} · ` : ''}“{item.evidence_anchor.quote}”
                </Text>
              )}
              {!!item.success_indicator && (
                <Text selectable style={styles.body}>
                  {t('report.successIndicator', { text: item.success_indicator })}
                </Text>
              )}
            </View>
          ))}
        </View>
      )}
      {plan.mentorQuestions.length > 0 && (
        <View style={styles.subBlock}>
          <Text style={styles.subTitle}>{t('report.mentorQuestionsTitle')}</Text>
          {plan.mentorQuestions.map((item, index) => (
            <View key={`question-${index}`} style={styles.stepRow}>
              <MaterialIcons name="help-outline" size={18} color={reviewDesk.accent} />
              <Text selectable style={[styles.body, styles.flexOne]}>{item.question}</Text>
            </View>
          ))}
        </View>
      )}
      {!!plan.boundary && <Text style={styles.metaText}>{plan.boundary}</Text>}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Body (summary first on phones; two columns on desktop)
// ---------------------------------------------------------------------------

export function ReportBody({
  structured,
  onCompetencyToggle,
}: {
  structured: PersistedAnalysisStructured;
  onCompetencyToggle?: CompetencyToggle;
}) {
  const { isDesktop } = useResponsive();
  if (!isDesktop) {
    return (
      <View style={styles.stack}>
        <ReportSummary structured={structured} />
        <CompetencyList structured={structured} onToggle={onCompetencyToggle} />
        <DevelopmentPlan structured={structured} />
      </View>
    );
  }
  return (
    <View style={styles.columns}>
      <View style={[styles.stack, styles.mainColumn]}>
        <ReportSummary structured={structured} />
        <CompetencyList structured={structured} onToggle={onCompetencyToggle} />
      </View>
      <View style={[styles.stack, styles.sideColumn]}>
        <DevelopmentPlan structured={structured} />
      </View>
    </View>
  );
}

export const reportStyles = StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: reviewDesk.card,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    borderRadius: 18,
    padding: 20,
    gap: 12,
  },
  cardPhone: { padding: 16, borderRadius: 14 },
  sectionTitle: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  body: { color: reviewDesk.inkSoft, fontSize: 15, lineHeight: 22 },
  metaText: { color: reviewDesk.muted, fontSize: 13, lineHeight: 19 },
  primaryButton: {
    minHeight: 44,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: reviewDesk.accent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  primaryButtonText: { color: '#FFFFFF', fontSize: 15, fontWeight: '800' },
  secondaryButton: {
    minHeight: 44,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  secondaryButtonText: { color: reviewDesk.accent, fontSize: 14, fontWeight: '800' },
});

const styles = StyleSheet.create({
  ...reportStyles,
  flexOne: { flex: 1, minWidth: 0 },
  stack: { width: '100%', gap: 16 },
  columns: { flexDirection: 'row', alignItems: 'flex-start', gap: 20, width: '100%' },
  mainColumn: { flex: 1.7, minWidth: 0 },
  sideColumn: { flex: 1, minWidth: 0 },
  header: { gap: 16 },
  headerTop: { flexDirection: 'row', gap: 20, alignItems: 'flex-start' },
  headerTopPhone: { flexDirection: 'column', gap: 14 },
  headerActions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10, width: '100%' },
  eyebrow: { color: reviewDesk.accent, fontSize: 12, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase' },
  title: { color: reviewDesk.ink, fontSize: 28, lineHeight: 35, fontWeight: '800', marginTop: 4 },
  titlePhone: { fontSize: 22, lineHeight: 28 },
  levelBlock: { alignItems: 'flex-end', gap: 6 },
  levelBlockPhone: { alignItems: 'flex-start' },
  levelLabel: { color: reviewDesk.muted, fontSize: 12, fontWeight: '700', textTransform: 'uppercase' },
  menteeHeadline: { color: reviewDesk.ink, fontSize: 16, lineHeight: 22, fontWeight: '800' },
  statusChip: {
    backgroundColor: reviewDesk.accentSoft,
    color: reviewDesk.accent,
    fontWeight: '700',
    fontSize: 13,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    overflow: 'hidden',
  },
  badge: { alignSelf: 'flex-start', borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4 },
  badgeLarge: { paddingHorizontal: 16, paddingVertical: 8 },
  badgeText: { fontSize: 13, fontWeight: '800' },
  badgeTextLarge: { fontSize: 17 },
  policyChip: { backgroundColor: reviewDesk.cardMuted, borderColor: reviewDesk.lineStrong },
  policyText: { color: reviewDesk.inkSoft },
  concernChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: reviewDesk.amberSoft,
    borderColor: reviewDesk.amber,
  },
  concernText: { color: reviewDesk.ink },
  accentCard: { backgroundColor: reviewDesk.accentWash, borderColor: reviewDesk.accentSoft },
  lead: { color: reviewDesk.ink, fontSize: 17, lineHeight: 25, fontWeight: '800' },
  bodyStrong: { color: reviewDesk.ink, fontSize: 15, lineHeight: 22, fontWeight: '700' },
  notice: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
    backgroundColor: reviewDesk.amberSoft,
    borderRadius: 12,
    padding: 12,
  },
  stepRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: reviewDesk.accent,
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 24,
    overflow: 'hidden',
  },
  subBlock: { gap: 8, marginTop: 4 },
  subTitle: { color: reviewDesk.ink, fontSize: 14, fontWeight: '800' },
  strengthTitle: { color: reviewDesk.success },
  developmentTitle: { color: '#9A6500' },
  competency: { borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 10, gap: 10 },
  competencyHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44 },
  code: { color: reviewDesk.muted, fontSize: 13, fontWeight: '800', width: 26 },
  competencyName: { color: reviewDesk.ink, fontSize: 15, fontWeight: '700' },
  competencyBody: { gap: 10, paddingLeft: 36, paddingBottom: 6 },
  competencyBodyPhone: { paddingLeft: 0 },
  quoteCard: { borderLeftWidth: 3, borderRadius: 12, padding: 12, gap: 6, backgroundColor: reviewDesk.cardMuted },
  quoteStrength: { borderLeftColor: reviewDesk.success },
  quoteDevelopment: { borderLeftColor: reviewDesk.amber },
  quoteMeta: { flexDirection: 'row', gap: 8 },
  timestamp: { color: reviewDesk.muted, fontSize: 13, fontWeight: '700' },
  quote: { color: reviewDesk.ink, fontSize: 15, lineHeight: 22, fontStyle: 'italic' },
  planItem: { gap: 4, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 10 },
  pressed: { opacity: 0.7 },
});
